/**
 * Hooks for the customer-mapping flow on the Faire Issues page.
 *
 * useFaireCustomerSearch(query): debounced sage_ar_customer_mirror search
 * useFaireCustomerMap(): mutation that writes the retailer -> customer
 *                        mapping, optionally flips a state row, optionally
 *                        marks an error resolved.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, apiPost } from './api';
import { FAIRE_API_BASE } from './useFaireRuns';

export type SageCustomerRow = {
  CustomerNo: string;
  CustomerName: string;
  AddressLine1: string | null;
  City: string | null;
  State: string | null;
  ZipCode: string | null;
  TelephoneNo: string | null;
};

export type CustomerSearchResponse = {
  customers: SageCustomerRow[];
};

export function useFaireCustomerSearch(
  query: string,
  opts?: { state?: string; enabled?: boolean },
): UseQueryResult<CustomerSearchResponse, Error> & { customers: SageCustomerRow[] } {
  const trimmed = query.trim();
  const enabled = (opts?.enabled ?? true) && trimmed.length >= 2;
  const q = useQuery<CustomerSearchResponse, Error>({
    queryKey: ['faire-customer-search', trimmed, opts?.state ?? ''],
    queryFn: () => {
      const params = new URLSearchParams({ q: trimmed });
      if (opts?.state) params.set('state', opts.state);
      return apiGet<CustomerSearchResponse>(`${FAIRE_API_BASE}/customers/search?${params.toString()}`);
    },
    enabled,
    staleTime: 30_000,
  });
  return Object.assign(q, { customers: q.data?.customers ?? [] });
}

export type CustomerMapRequest = {
  faire_retailer_id: string;
  sage_customer_no: string;
  business_name?: string;
  mapped_city?: string;
  mapped_state?: string;
  resolve_error_id?: number;
  update_state_for_faire_order_id?: string;
};

export type CustomerMapResponse = {
  ok: boolean;
  faire_retailer_id: string;
  sage_customer_no: string;
  sage_customer_name: string;
  state_rows_updated: number;
  error_resolved: number;
};

export type FaireRetailerLookupResponse = {
  retailer_id: string;
  name: string | null;
  address: {
    line_1: string | null;
    line_2: string | null;
    city: string | null;
    state_region: string | null;
    postal_code: string | null;
    country: string | null;
  };
};

export function useFaireRetailerLookup(retailerId: string | null, orderId: string | null) {
  return useQuery<FaireRetailerLookupResponse, Error>({
    queryKey: ['faire-retailer-lookup', retailerId, orderId],
    queryFn: () => {
      const params = new URLSearchParams({ order_id: orderId ?? '' });
      return apiGet<FaireRetailerLookupResponse>(
        `${FAIRE_API_BASE}/channels/faire/retailers/${encodeURIComponent(retailerId ?? '')}?${params.toString()}`,
      );
    },
    enabled: Boolean(retailerId) && Boolean(orderId),
    // Faire retailer addresses change rarely; cache aggressively to avoid
    // re-hitting Faire when the operator re-opens the same modal.
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // Don't retry on 4xx (retailer-id wrong = won't fix by retrying)
    retry: (failureCount, error) => {
      if (/^[45]\d\d/.test(error.message)) return false;
      return failureCount < 2;
    },
  });
}

export function useFaireCustomerMap() {
  const qc = useQueryClient();
  return useMutation<CustomerMapResponse, Error, CustomerMapRequest>({
    mutationFn: (req) => apiPost<CustomerMapResponse>(`${FAIRE_API_BASE}/customers/map`, req),
    onSuccess: () => {
      // Refresh errors + dashboard counts so the resolved error disappears
      // and the dashboard "awaiting customer mapping" tile drops.
      qc.invalidateQueries({ queryKey: ['faire-errors'] });
      qc.invalidateQueries({ queryKey: ['faire-shipment-counts'] });
    },
  });
}
